import { useId, useRef, useState, type DragEvent } from 'react';
import {
  ArrowRight,
  ClipboardPaste,
  FileText,
  FlaskConical,
  TriangleAlert,
  Upload,
  X,
} from 'lucide-react';
import { create } from 'zustand';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Disclosure,
  HelpText,
  IconButton,
  Label,
  Select,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Textarea,
  Tr,
  cn,
} from '@/components/ui';
import { UNIT_OPTIONS } from '@/components/common/units';
import type { SystemPoint } from '@/types';
import { parseCoordinateData, type SkippedLine } from '@/utils/fileParser';

type SystemNumber = 1 | 2;

interface ExampleOption {
  key: string;
  name: string;
  description: string;
}

interface ImportTabProps {
  system1Points: SystemPoint[];
  system2Points: SystemPoint[];
  onPointsLoaded: (points: SystemPoint[], systemNumber: SystemNumber) => void;
  /** Remove one dataset (clears pairs and results). */
  onClearPoints?: (systemNumber: SystemNumber) => void;
  /** Project units (labels only); the units control shows with a handler. */
  units?: string;
  onUnitsChange?: (units: string) => void;
  /** Shown once both datasets are loaded. */
  onContinue?: () => void;
  /** Offered while both datasets are empty. */
  examples?: ReadonlyArray<ExampleOption>;
  onLoadExample?: (key: string) => void;
}

/** What one parse produced: accepted points and per-line rejections. */
interface ParsedImport {
  points: SystemPoint[];
  fileName: string | null;
  skipped: SkippedLine[];
}

/**
 * The import report of each loaded dataset, kept outside the component so
 * the file name and rejected rows survive switching workflow steps. A report
 * applies only while the store still holds that exact points array; any
 * other replacement (example, project, undo) hides it.
 */
const useImportReports = create<Partial<Record<SystemNumber, ParsedImport>>>(
  () => ({})
);

const SYSTEMS = {
  1: {
    name: 'Record',
    system: 'System 1',
    description: 'Plat or deed positions, transformed into the Field frame.',
    dot: 'bg-record',
  },
  2: {
    name: 'Field',
    system: 'System 2',
    description: 'Measured monuments, held as the fixed frame.',
    dot: 'bg-field',
  },
} as const;

const ACCEPTED_FILES = '.txt,.csv,.dat,.trd';
const PREVIEW_ROWS = 5;

const plural = (count: number, noun: string) =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

const RejectedRows = ({ skipped }: { skipped: SkippedLine[] }) => (
  <Disclosure
    variant="plain"
    icon={<TriangleAlert className="text-warning" />}
    title={`${plural(skipped.length, 'row')} rejected`}
    className="text-sm"
  >
    <ul className="max-h-40 space-y-1 overflow-auto rounded-md border border-border bg-surface-sunken px-3 py-2 text-xs">
      {skipped.map((line) => (
        <li key={line.lineNumber} className="flex gap-2">
          <span className="w-14 shrink-0 font-mono tabular-nums text-fg-muted">
            Line {line.lineNumber}
          </span>
          <span className="min-w-0">
            <span className="break-all font-mono text-fg">
              {line.content.length > 48
                ? `${line.content.slice(0, 48)}…`
                : line.content}
            </span>
            <span className="text-fg-muted"> — {line.reason}</span>
          </span>
        </li>
      ))}
    </ul>
  </Disclosure>
);

const PointsTable = ({
  points,
  maxRows,
  showDescription = false,
  className,
}: {
  points: SystemPoint[];
  maxRows?: number;
  showDescription?: boolean;
  className?: string;
}) => {
  const rows = maxRows ? points.slice(0, maxRows) : points;
  return (
    <Table stickyHeader containerClassName={className}>
      <THead>
        <Tr>
          <Th className="h-8">Point</Th>
          <Th numeric className="h-8">
            North
          </Th>
          <Th numeric className="h-8">
            East
          </Th>
          {showDescription && <Th className="h-8">Description</Th>}
        </Tr>
      </THead>
      <TBody>
        {rows.map((point) => (
          <Tr key={point.point}>
            <Td mono className="h-8 py-1">
              {point.point}
            </Td>
            <Td numeric className="h-8 py-1">
              {point.north}
            </Td>
            <Td numeric className="h-8 py-1">
              {point.east}
            </Td>
            {showDescription && (
              <Td className="h-8 max-w-[10rem] truncate py-1 text-fg-muted">
                {point.description}
              </Td>
            )}
          </Tr>
        ))}
      </TBody>
    </Table>
  );
};

const DatasetCard = ({
  systemNumber,
  points,
  onPointsLoaded,
  onClearPoints,
}: {
  systemNumber: SystemNumber;
  points: SystemPoint[];
  onPointsLoaded: (points: SystemPoint[], systemNumber: SystemNumber) => void;
  onClearPoints?: (systemNumber: SystemNumber) => void;
}) => {
  const meta = SYSTEMS[systemNumber];
  const fileRef = useRef<HTMLInputElement>(null);
  const readVersion = useRef(0);
  const formatsId = useId();
  const [isDragOver, setIsDragOver] = useState(false);
  const [pending, setPending] = useState<ParsedImport | null>(null);
  const [failed, setFailed] = useState<ParsedImport | null>(null);
  const [readError, setReadError] = useState<string | null>(null);
  const [pasteText, setPasteText] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const storedReport = useImportReports((s) => s[systemNumber]);
  const report = storedReport?.points === points ? storedReport : null;

  const loadContent = (content: string, fileName: string | null) => {
    const { points: parsed, skipped } = parseCoordinateData(
      content,
      systemNumber
    );
    const result = { points: parsed, fileName, skipped };
    setReadError(null);
    if (parsed.length > 0) {
      setPending(result);
      setFailed(null);
    } else {
      setPending(null);
      setFailed(result);
    }
  };

  const readFile = (file: File) => {
    const version = ++readVersion.current;
    const reader = new FileReader();
    reader.onerror = () => {
      if (version !== readVersion.current) return;
      setPending(null);
      setReadError(
        `Could not read ${file.name}. Your current points are unchanged.`
      );
    };
    reader.onload = (event) => {
      if (version !== readVersion.current) return;
      loadContent((event.target?.result as string) ?? '', file.name);
    };
    reader.readAsText(file);
  };

  const browse = () => fileRef.current?.click();

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) readFile(file);
  };

  const previewPaste = () => {
    if (!pasteText.trim()) return;
    readVersion.current++;
    loadContent(pasteText, null);
    setPasteText('');
    setPasteOpen(false);
  };

  const acceptPending = () => {
    if (!pending) return;
    onPointsLoaded(pending.points, systemNumber);
    useImportReports.setState({ [systemNumber]: pending });
    setPending(null);
  };

  return (
    <Card
      role="region"
      aria-label={`${meta.name} dataset`}
      className={cn(
        'flex flex-col transition-[border-color,box-shadow] duration-150',
        isDragOver && 'border-accent ring-2 ring-accent/20'
      )}
      onDragOver={(event) => {
        event.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null))
          setIsDragOver(false);
      }}
      onDrop={handleDrop}
    >
      <input
        type="file"
        ref={fileRef}
        accept={ACCEPTED_FILES}
        className="hidden"
        aria-hidden="true"
        tabIndex={-1}
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) readFile(file);
          event.target.value = '';
        }}
      />
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className={cn('size-2.5 shrink-0 rounded-full', meta.dot)}
          />
          {meta.name}
          <span className="font-normal text-fg-muted">{meta.system}</span>
        </CardTitle>
        <CardDescription>{meta.description}</CardDescription>
      </CardHeader>

      <CardBody className="flex flex-1 flex-col gap-3">
        {readError && (
          <Alert tone="danger" onDismiss={() => setReadError(null)}>
            {readError}
          </Alert>
        )}
        {failed && (
          <Alert
            tone="danger"
            title={`No points could be read${failed.fileName ? ` from ${failed.fileName}` : ''}`}
            onDismiss={() => setFailed(null)}
          >
            <p>
              Expected rows of Point, North, East, then optional Elevation and
              Description. Your current points are unchanged.
            </p>
            {failed.skipped.length > 0 && (
              <div className="mt-2">
                <RejectedRows skipped={failed.skipped} />
              </div>
            )}
          </Alert>
        )}

        {pending ? (
          <section
            aria-label={`${meta.name} import preview`}
            className="flex flex-1 flex-col gap-3 rounded-lg border border-accent/30 bg-accent-muted/40 p-3"
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-fg">Review import</p>
                <p className="truncate text-xs text-fg-muted">
                  {pending.fileName ?? 'Pasted rows'}
                </p>
              </div>
              <div className="flex gap-1.5">
                <Badge tone="accent">{pending.points.length} accepted</Badge>
                <Badge tone={pending.skipped.length ? 'warning' : 'neutral'}>
                  {pending.skipped.length} rejected
                </Badge>
              </div>
            </div>
            {points.length > 0 && (
              <p className="text-xs text-fg-muted">
                Replaces {plural(points.length, `${meta.name} point`)} and
                clears pairs and results. You can undo afterwards.
              </p>
            )}
            <PointsTable
              points={pending.points}
              showDescription
              className="max-h-56 rounded-md border border-border bg-surface"
            />
            {pending.skipped.length > 0 && (
              <RejectedRows skipped={pending.skipped} />
            )}
            <div className="mt-auto flex flex-wrap justify-end gap-2 pt-1">
              <Button
                variant="ghost"
                onClick={() => {
                  readVersion.current++;
                  setPending(null);
                }}
              >
                Cancel import
              </Button>
              <Button variant="primary" onClick={acceptPending}>
                Import {pending.points.length} {meta.name} points
              </Button>
            </div>
          </section>
        ) : points.length > 0 ? (
          <div className="flex flex-1 flex-col gap-3">
            <div className="flex items-center gap-3">
              <span
                aria-hidden="true"
                className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface-sunken text-fg-muted [&_svg]:size-4"
              >
                <FileText />
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-fg">
                  {plural(points.length, 'point')}
                </p>
                <p className="truncate text-xs text-fg-muted">
                  {report?.fileName ??
                    (report ? 'Pasted rows' : 'Current project')}
                </p>
              </div>
              <Button size="sm" leadingIcon={<Upload />} onClick={browse}>
                Replace…
              </Button>
              {onClearPoints && (
                <IconButton
                  size="sm"
                  aria-label={`Clear ${meta.name} points`}
                  icon={<X />}
                  onClick={() => onClearPoints(systemNumber)}
                />
              )}
            </div>
            <div className="overflow-hidden rounded-md border border-border">
              <PointsTable points={points} maxRows={PREVIEW_ROWS} />
              {points.length > PREVIEW_ROWS && (
                <p className="border-t border-border bg-surface-sunken px-3 py-1.5 text-xs text-fg-muted">
                  and {points.length - PREVIEW_ROWS} more
                </p>
              )}
            </div>
            {report && report.skipped.length > 0 && (
              <RejectedRows skipped={report.skipped} />
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={browse}
            aria-label={`Choose a ${meta.name} coordinate file`}
            aria-describedby={formatsId}
            className={cn(
              'group flex min-h-36 flex-1 flex-col items-center justify-center gap-3 rounded-lg border border-dashed px-6 py-6 text-center transition-colors duration-150 sm:min-h-44 sm:py-8',
              isDragOver
                ? 'border-accent bg-accent-muted/60'
                : 'border-border-strong bg-surface-sunken/60 hover:border-accent/50 hover:bg-accent-muted/30'
            )}
          >
            <span
              aria-hidden="true"
              className="flex size-10 items-center justify-center rounded-lg border border-border bg-surface text-fg-muted shadow-sm transition-colors group-hover:text-accent [&_svg]:size-5"
            >
              <Upload />
            </span>
            <span className="text-sm">
              <span className="font-medium text-fg">
                {isDragOver ? 'Drop to preview' : 'Drop a file here'}
              </span>
              {!isDragOver && (
                <>
                  <span className="text-fg-muted"> or </span>
                  <span className="font-medium text-accent">browse</span>
                </>
              )}
            </span>
            <span id={formatsId} className="text-xs text-fg-muted">
              CSV, TXT, DAT, or TRD · Point, North, East
            </span>
          </button>
        )}

        {!pending && (
          <Disclosure
            variant="plain"
            icon={<ClipboardPaste />}
            open={pasteOpen}
            onOpenChange={setPasteOpen}
            title={
              <>
                Paste rows
                <span className="sr-only"> for {meta.name}</span>
              </>
            }
            className="mt-auto"
          >
            <div className="space-y-2">
              <Textarea
                mono
                rows={4}
                aria-label={`Paste ${meta.name} coordinates`}
                value={pasteText}
                onChange={(event) => setPasteText(event.target.value)}
                placeholder={
                  '402, 3972.2209, 5042.8287, IP FOUND\n403, 2748.9233, 5023.7539'
                }
                className="text-xs sm:text-xs"
              />
              <div className="flex items-center justify-between gap-3">
                <HelpText>
                  One point per row, from a spreadsheet or CAD report.
                </HelpText>
                <Button
                  size="sm"
                  onClick={previewPaste}
                  disabled={!pasteText.trim()}
                >
                  Preview pasted rows
                </Button>
              </div>
            </div>
          </Disclosure>
        )}
      </CardBody>
    </Card>
  );
};

/**
 * Import step: one card per dataset (drop, browse, or paste; preview before
 * accepting), project units, an example-project shortcut while empty, and
 * the hand-off to Adjust once both datasets are loaded.
 */
export const ImportTab = ({
  system1Points,
  system2Points,
  onPointsLoaded,
  onClearPoints,
  units,
  onUnitsChange,
  onContinue,
  examples,
  onLoadExample,
}: ImportTabProps) => {
  const bothLoaded = system1Points.length > 0 && system2Points.length > 0;
  const bothEmpty = system1Points.length === 0 && system2Points.length === 0;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6 sm:px-6 sm:py-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl space-y-1">
          <h2 className="text-base font-semibold text-fg">
            Import coordinates
          </h2>
          <p className="text-sm text-fg-muted">
            Load each dataset as PNEZD rows, comma, space, or tab delimited.
            Elevation is optional and ignored.
          </p>
        </div>
        {units !== undefined && onUnitsChange && (
          <div className="flex shrink-0 flex-col gap-1 sm:items-end">
            <div className="flex items-center gap-2">
              <Label htmlFor="project-units" className="shrink-0">
                Project units
              </Label>
              <Select
                id="project-units"
                controlSize="sm"
                containerClassName="w-44"
                aria-describedby="project-units-help"
                value={units}
                onChange={(event) => onUnitsChange(event.target.value)}
              >
                {UNIT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </div>
            <HelpText id="project-units-help">
              Labels only. Coordinates are never converted.
            </HelpText>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-6">
        <DatasetCard
          systemNumber={1}
          points={system1Points}
          onPointsLoaded={onPointsLoaded}
          onClearPoints={onClearPoints}
        />
        <DatasetCard
          systemNumber={2}
          points={system2Points}
          onPointsLoaded={onPointsLoaded}
          onClearPoints={onClearPoints}
        />
      </div>

      {bothEmpty && examples && examples.length > 0 && onLoadExample && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FlaskConical
                aria-hidden="true"
                className="size-4 text-fg-muted"
              />
              Try an example project
            </CardTitle>
            <CardDescription>
              Sample Record and Field data with matched pairs, ready to solve.
            </CardDescription>
          </CardHeader>
          <CardBody className="pt-3">
            <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border">
              {examples.map((example) => (
                <li
                  key={example.key}
                  className="flex items-center justify-between gap-3 px-3 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-fg">
                      {example.name}
                    </p>
                    <p className="truncate text-xs text-fg-muted">
                      {example.description}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    aria-label={`Load ${example.name} example`}
                    onClick={() => onLoadExample(example.key)}
                  >
                    Load
                  </Button>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {bothLoaded && onContinue && (
        <div className="sticky bottom-4 z-10 flex flex-col gap-3 rounded-xl border border-border bg-surface-raised/95 px-4 py-3 shadow-lg backdrop-blur sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <p className="text-sm">
            <span className="font-medium text-fg">Both datasets loaded.</span>{' '}
            <span className="text-fg-muted">
              {system1Points.length} Record · {system2Points.length} Field
              points
            </span>
          </p>
          <Button
            variant="primary"
            trailingIcon={<ArrowRight />}
            onClick={onContinue}
          >
            Continue to Adjust
          </Button>
        </div>
      )}
    </div>
  );
};
